/**
 * useCelebration — WS subscriber + dispatch (Plan 02-15 / D-10 / CEL-01..03).
 *
 * Opens a WebSocket to `/ws`, subscribes to the 'celebrations' and 'chores'
 * topics, and translates verb-only frames into celebration events that the
 * provider routes to its tier renderers (L1/L2/L3/L4).
 *
 * D-10: triggers come from WS events ONLY — never from request resolution.
 * The claim-race loser receives `status_changed` with `claimed_by !==
 * viewerId`, so the L1 dispatch short-circuits before firing. The winner
 * receives `claimed_by === viewerId` → L1 fires.
 *
 * Tier mapping per UI-SPEC §11:
 *   - chore.claimed (status='claimed', claimed_by===viewerId) → L1
 *   - chore.completed (own complete; UI-SPEC says "fires immediately on
 *     submit since complete is optimistic per D-12") → L2 fires from the
 *     submit handler, NOT from the WS path. The hook still surfaces a
 *     status_changed→awaiting_approval event for the WS-driven claim
 *     winner so the L2 component can render briefly if needed.
 *   - chore.approved (status='completed', claimed_by===viewerId) → L3.
 *     'completed' is the post-approval DB enum value and what the server
 *     actually puts on the wire. On this one verb `approved_by` (the admin)
 *     and `claimed_by` (the kid who earned) are different people; the gate
 *     below is on `claimed_by`.
 *   - achievement.unlocked (memberId===viewerId) → L4
 *
 * Issue #86: the chore frame is no longer described by a local interface that
 * happened to agree with the server. It is `ChoreStatusFrame` from
 * @stell/shared — the same module apps/api constructs every broadcast
 * through — so the two halves cannot drift apart again. They did twice, each
 * time killing a whole tier for months with both suites green: #79 (approval
 * frame lacked the earner + the celebration fields, L3 dead) and #83 (claim
 * frame said `by` where this hook read `claimed_by`, L1 dead).
 *
 * Issue #92 is the same fix on the OTHER topic: the achievement frame was a
 * local interface too, with every field optional, and the server had never
 * sent `memberId` at all — so the L4 gate compared `undefined !== viewerId`,
 * which is always true, and the tier was dead for ~4.5 months. It is now
 * `AchievementUnlockedFrame` from @stell/shared.
 *
 * The parse on both branches is deliberately the LOOSE schema while the server
 * constructs against the strict one. A strict parse on this side would mean
 * that the day the server adds a field, every Workbox-cached old client
 * rejects the entire frame and the tier dies again — the exact failure mode
 * being fixed.
 *
 * INV-RM-1: every event payload carries `reducedMotion` so the tier
 * renderer knows whether to use the static fallback. The hook reads
 * `prefers-reduced-motion: reduce` once on dispatch via matchMedia and
 * stamps the boolean on the event.
 *
 * Gjenoppkobling og backoff: se lib/reconnectingSocket.ts.
 */
import { useEffect, useRef } from 'react';
import { AchievementUnlockedFrame, ChoreStatusFrame } from '@stell/shared';
import { playSfx } from './audio';
import type { ViewportClass } from './timings';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

/**
 * Monotonic per-session counter behind `nextEventId`. Module-scoped rather
 * than per-hook so two provider mounts can never collide.
 */
let _seq = 0;

/**
 * Mint the identity CelebrationProvider keys its tier children on (issue #82).
 *
 * Nothing already in the payload can serve: `instanceId` repeats when a chore
 * is re-approved, `achievementKey` repeats whenever the same achievement is
 * emitted twice, and the queue's array index shifts the moment its head is
 * removed — which is precisely the bug. A React key that is not stable per
 * event remounts a live celebration, resetting every once-per-mount ref guard
 * behind it (issue #81), or — worse, for L4 — fails to change at all and hides
 * the next item in the queue entirely.
 *
 * A counter, not crypto.randomUUID: the id is never persisted, never leaves
 * the tab, and carries no meaning beyond "not the other one". Uniqueness
 * within one page session is the whole requirement, and a counter states that
 * in a line without pulling in a crypto dependency for a React key.
 */
function nextEventId(): string {
  return `cel-${++_seq}`;
}

export type CelebrationTier = 'l1' | 'l2' | 'l3' | 'l4';

export interface CelebrationEvent {
  /**
   * Unique per dispatched event, stamped by the hook. CelebrationProvider uses
   * it as the React key for the tier child — see `nextEventId` for why no
   * other field will do.
   */
  id: string;
  tier: CelebrationTier;
  reducedMotion: boolean;
  viewport: ViewportClass;
  /** For L1/L2/L3 — the chore instance id. */
  instanceId?: string | undefined;
  /** For L3 — points awarded by approval. */
  points?: number | undefined;
  /** For L3 — the claimer's balance BEFORE the award, for the wallet count-up. */
  prevBalance?: number | undefined;
  /** For L3 — the admin who approved (`approved_by`), slått opp til et navn (#155). */
  approvedBy?: string | undefined;
  /**
   * For L3 — oppgavetittelen. Rammen har den ikke; CelebrationProvider fyller
   * den inn fra spørringsbufferen når hendelsen kommer (#155).
   */
  title?: string | undefined;
  /** For L4 — achievement key from registry. */
  achievementKey?: string | undefined;
  /** For L4 — unlocked-at ISO timestamp. */
  unlockedAt?: string | undefined;
}

/**
 * What a tier mapping builds: everything but the identity, which only
 * `dispatch` may mint. Keeping the id out of the local's type is what stops a
 * future mapping from inventing one of its own.
 */
export type CelebrationEventInput = Omit<CelebrationEvent, 'id'>;

export interface UseCelebrationOpts {
  viewerId: string | null;
  viewport: ViewportClass;
  /** Optional callback fired for every dispatched event. Provider passes a queue updater. */
  onEvent?: (ev: CelebrationEvent) => void;
  /** Disable automatic SFX firing (provider handles SFX in tier components). */
  silent?: boolean;
}

function isReducedMotion(): boolean {
  if (
    typeof globalThis.matchMedia !== 'function' &&
    (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
  ) {
    return false;
  }
  const mm =
    typeof globalThis.matchMedia === 'function'
      ? globalThis.matchMedia
      : window.matchMedia;
  try {
    return mm('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

export function useCelebration(opts: UseCelebrationOpts): void {
  const { viewerId, viewport, onEvent, silent } = opts;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!viewerId) return undefined;

    // The single point where a celebration acquires its identity. Every tier
    // mapping below builds a CelebrationEventInput and hands it here.
    const dispatch = (ev: CelebrationEventInput) => {
      onEventRef.current?.({ ...ev, id: nextEventId() });
    };

    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: unknown;
        };

        const reducedMotion = isReducedMotion();

        if (msg?.topic === 'celebrations' && msg?.event === 'achievement_unlocked') {
          // Issue #92: parse the SHARED schema the server constructs against,
          // so a missing field is a dropped frame rather than a dead tier. The
          // server used to send no `memberId` at all, which made the gate
          // below `undefined !== viewerId` — always true, L4 never fired.
          // Deliberately the LOOSE schema: a newer server that adds a field
          // must not make this cached client throw the whole frame away.
          const parsed = AchievementUnlockedFrame.safeParse(msg.data);
          if (!parsed.success) return;
          const payload = parsed.data;

          // D-10: only the viewer's OWN unlocks trigger a celebration.
          // `hub.broadcastToMember` already filters, so this gate is redundant
          // today; it is kept because L3 gates identically on `claimed_by` and
          // a future swap to plain `hub.broadcast` would otherwise leak
          // someone else's achievement onto every screen in the house.
          if (payload.memberId !== viewerId) return;

          const evOut: CelebrationEventInput = {
            tier: 'l4',
            reducedMotion,
            viewport,
            achievementKey: payload.key,
            unlockedAt: payload.unlockedAt,
          };
          dispatch(evOut);
          if (!silent) {
            playSfx('achievement-takeover', { reducedMotion });
          }
          return;
        }

        if (msg?.topic === 'chores' && msg?.event === 'status_changed') {
          // Issue #86: parse the SHARED schema the server constructs against,
          // so a renamed field is a compile error rather than a dead tier.
          // Deliberately the LOOSE union: a newer server that adds a field
          // must not make this cached client throw the whole frame away.
          const parsed = ChoreStatusFrame.safeParse(msg.data);
          if (!parsed.success) return;
          const payload = parsed.data;

          // D-10: only the viewer's OWN events trigger celebrations. The gate
          // lives inside each branch, not above them: `claimed_by` exists
          // only on the claimed / awaiting_approval / completed members, and
          // the five pool-return verbs carry no actor at all.
          if (payload.status === 'claimed') {
            if (payload.claimed_by !== viewerId) return;
            const evOut: CelebrationEventInput = {
              tier: 'l1',
              reducedMotion,
              viewport,
              instanceId: payload.id,
            };
            dispatch(evOut);
            if (!silent) {
              playSfx('lock-in', { reducedMotion });
            }
            return;
          }

          if (payload.status === 'completed') {
            if (payload.claimed_by !== viewerId) return;
            const evOut: CelebrationEventInput = {
              tier: 'l3',
              reducedMotion,
              viewport,
              instanceId: payload.id,
              points: payload.points,
              prevBalance: payload.prevBalance,
              approvedBy: payload.approved_by,
            };
            dispatch(evOut);
            if (!silent) {
              playSfx('approved', { reducedMotion });
            }
            return;
          }

          // status='awaiting_approval' is not WS-driven for L2 (D-12 says
          // L2 fires from the optimistic submit handler). We deliberately
          // do nothing here so a slow-arriving server status_changed
          // doesn't fire a duplicate L2.
        }

        // Unknown topic / verb — ignore silently for forward compat.
      } catch {
        // Malformed JSON — drop silently per Plan 1 P15 pattern.
      }
    };

    return openReconnectingSocket({ topics: ['celebrations', 'chores'], onMessage });
  }, [viewerId, viewport, silent]);
}
